<template>
  <Card v-bind="$attrs" class="residential-account-shop">
    <template #header>
      <h2>{{ $tc('shop', 1) }}</h2>
    </template>

    <div
      v-if="!partials.length && status !== 'signed' && contract && contract.expiresAt"
      class="residential-account-shop__status"
    >
      <b>{{ $t('wholeShop') }}:</b>
      <ExpiresAt :value="contract.expiresAt" />
    </div>
    <DataTable theme="border" :header="false" :columns="[{ id: 'label' }, { id: 'status' }]" :value="categories">
      <template #label="{ item }">
        <div class="row middle between" style="width: 100%">
          <span>{{ item.label }}</span>
          <FontAwesomeIcon :icon="['fal', 'long-arrow-right']" />
        </div>
      </template>
      <template #status="{ item }">
        <CategoryStatus :value="item" :shop="shop" />
      </template>
    </DataTable>

    <template #footer>
      <table class="grow-1">
        <tr>
          <td>
            <strong>{{ $t('shopTotalLabel') }}</strong>
          </td>
          <td class="pl-1 text--right">{{ Currency($path('shop.total.gross', reference)) }}</td>
        </tr>
        <template v-if="$path('sale.value', reference)">
          <tr class="text--small">
            <td>{{ $path('name', reference) }}</td>
            <td class="pl-1 text--right">{{ Currency($path('sale.value', reference)) }}</td>
          </tr>
          <tr class="text--small">
            <td>{{ $t('priceIncShop') }}</td>
            <td class="pl-1 text--right">
              {{ Currency($path('sale.value', reference) + $path('shop.total.gross', reference)) }}
            </td>
          </tr>
        </template>
        <tr>
          <td colspan="2" class="text--small fade">{{ $t('pricesIncVat') }}</td>
        </tr>
      </table>
      <ButtonComponent
        :label="$t('gotoShop')"
        :icon="['fal', 'arrow-to-right']"
        :disabled="!$glob(contract && contract.status, 'unsigned signed')"
        tag="a"
        :href="contract ? '//' + contract.signUrl : undefined"
        target="_blank"
        theme="secondary"
      />
    </template>
  </Card>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'

import StatusLabel from '@/components/StatusLabel'
import { Currency } from '@/filters'
import Card from '@/components/Card'
import CategoryStatus from '@/views/Shop/Dashboard/Widgets/CategoryStatus'
import ExpiresAt from './ExpiresAt'
import DataTable from '@/components/DataTable'

const IconMap = {
  signed: ['fas', 'check-circle'],
  unsigned: ['fal', 'hourglass-start'],
  expired: ['fas', 'exclamation-triangle'],
}

const ThemeMap = {
  signed: 'success',
  unsigned: 'warning',
  expired: 'error',
  default: 'default',
}

const SignedStates = ['accepted', 'signed']

export default {
  computed: {
    ...mapState('ResidentialAccount', {
      item: 'item',
      reference: state => state.item.reference,
      shop: state => state.item.reference.shop,
    }),

    contract() {
      return this.$path('reference.shop.contract')
    },

    partials() {
      return this.$path('shop.contract.data.partials', this.reference) || []
    },
    categories() {
      return this.$path('shop.categories', this.reference) || []
    },
    icon() {
      return IconMap[this.status]
    },
    theme() {
      return ThemeMap[this.status] || ThemeMap.default
    },
    totalSignedCategories() {
      return this.categories.filter(c => SignedStates.includes(c.status)).length
    },

    master() {
      let signableFrom = this.contract && this.contract.data.signableFrom
      let expiresAt = this.contract && this.contract.expiresAt
      let now = moment()

      return {
        isSigned: this.contract && SignedStates.includes(this.contract.status),
        isSignable: signableFrom && moment(signableFrom).isBefore(now),
        isExpired: expiresAt && moment(expiresAt).isBefore(now),
        isDraft: this.contract && this.contract.status === 'draft',
      }
    },
    status() {
      if (this.reference.status !== 'sold') return 'notready'

      if (this.master.expired || this.categories.some(c => c.status === 'expired')) return 'expired'
      if (this.master.isSigned || (this.categories.length && this.totalSignedCategories === this.categories.length))
        return 'signed'
      if (this.master.isSignable || this.categories.some(c => c.status === 'unsigned')) return 'unsigned'
      if (this.master.isDraft || this.categories.some(c => c.status === 'draft')) return 'draft'
    },
    statusLabel() {
      let signed = this.master.isSigned ? 1 : this.totalSignedCategories
      let status = () => {
        switch (this.status) {
          case 'draft':
            return this.$t('shopBuyerStatusDraft')
          case 'notready':
            return this.$t('projectShopsNotSold')
          default:
            if (this.master.isSigned) return this.$t('shopContractStatusSigned')
            return `${this.$t('shopContractStatusSigned')} ${signed}  ${this.$t('of')} ${
              this.partials.length ? this.categories.length : 1
            }`
        }
      }
      return `${this.$tc('status')} : ${status()}`
    },
    acceptedMessage() {
      if (this.status !== 'signed' || !this.contract) return
      switch (this.contract.status) {
        case 'accepted':
          return this.$t('contractStatusAcceptedBySeller')
        default:
          return this.$t('contractStatusNotAcceptedBySeller')
      }
    },
    additionalMessage() {
      if (this.acceptedMessage) return this.acceptedMessage
      if (this.status === 'expired') return this.$t('oneOrMoreDeadlinesExpired')
    },
  },
  methods: {
    Currency(val) {
      return Currency(val, { currency: this.reference.project.metadata.currency })
    },
  },
  components: {
    StatusLabel,
    CategoryStatus,
    Card,
    ExpiresAt,
    DataTable,
  },
}
</script>

<style lang="scss">
.residential-account-shop {
  & > .card__header {
    min-height: initial;
    flex-direction: row;
    justify-content: flex-start;

    & > svg {
      margin-right: 1.5rem;
    }

    h2 {
      margin: 0;

      @include respond-below('phone') {
        font-size: 1.2em;
      }
    }
  }

  .shop-summary-category-status {
    border-radius: 0;
    border: none;
    width: 100%;
  }

  position: relative;
  gap: 0.5rem;

  .card {
    &__content {
      display: flex;
      flex-direction: column;
    }

    &__footer-content {
      justify-content: space-between;
      align-items: flex-start;
      gap: 2rem;
    }
  }

  &__status {
    display: flex;
    justify-items: center;
    align-items: center;
    margin-bottom: 2rem;
    gap: 1rem;
  }

  .data-table {
    border-bottom: 1px solid var(--border-color);

    &__row:first-child .data-table__cell {
      border-top: 1px solid var(--border-color);
    }

    &__cell {
      padding: 0;

      &:first-child {
        padding-inline: 1rem;
        border-left: 1px solid var(--border-color);
      }

      &:last-child {
        padding-left: 1rem;
        border-right: 1px solid var(--border-color);
      }
    }
  }
}
</style>
